import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import { buildSeriesShows, showToCatalogItem } from "../series";

const MIN_Q = 2;
const LIMIT = 24;

function posterUrl(item) {
  return (
    item?.cover_url ||
    item?.series_poster_url ||
    item?.season_poster_url ||
    item?.poster_url ||
    item?.logo ||
    ""
  );
}

function typeLabel(item) {
  const t = item?.content_type || "";
  if (t === "movie") return "Película";
  if (t === "series") return "Serie";
  if (t === "anime_series") return "Anime";
  if (t === "anime") return "Anime";
  return "Vídeo";
}

function CatalogHit({ item }) {
  const img = posterUrl(item);
  const isSeries = item?.kind === "series_show";
  const to = isSeries
    ? item.catalog_href || `/series/${item.catalog_key || item.id}`
    : `/watch/${item.id}`;

  return (
    <Link to={to} className="search-hit search-hit-catalog">
      <div className="search-hit-thumb">
        {img ? (
          <img src={img} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="search-hit-fallback">▶</span>
        )}
      </div>
      <div className="search-hit-body">
        <strong>{item.title}</strong>
        <span className="search-hit-meta">{typeLabel(item)}</span>
      </div>
    </Link>
  );
}

function ChannelHit({ channel }) {
  return (
    <Link to={`/iptv?ch=${encodeURIComponent(channel.id)}`} className="search-hit search-hit-channel">
      <div className="search-hit-thumb search-hit-thumb-live">
        {channel.logo ? (
          <img src={channel.logo} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="search-hit-fallback">TV</span>
        )}
        <span className="search-hit-live">EN VIVO</span>
      </div>
      <div className="search-hit-body">
        <strong>{channel.name}</strong>
        <span className="search-hit-meta">
          {[channel.group, channel.country].filter(Boolean).join(" · ") || "Canal IPTV"}
        </span>
      </div>
    </Link>
  );
}

export default function SearchPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelTotal, setChannelTotal] = useState(0);

  useEffect(() => {
    if (!token || q.length < MIN_Q) {
      setVideos([]);
      setChannels([]);
      setChannelTotal(0);
      setError("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      api(`/api/videos?q=${encodeURIComponent(q)}`, { token }),
      api(
        `/api/iptv/channels?q=${encodeURIComponent(q)}&limit=${LIMIT}&offset=0`,
        { token }
      ),
    ])
      .then(([videoData, iptvData]) => {
        if (cancelled) return;
        setVideos(Array.isArray(videoData) ? videoData : []);
        setChannels((iptvData && iptvData.channels) || []);
        setChannelTotal(iptvData?.total_filtered ?? (iptvData?.channels || []).length);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "No se pudo buscar");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, q]);

  const grouped = useMemo(() => {
    const movies = videos.filter((v) => (v.content_type || "movie") === "movie");
    const seriesShows = buildSeriesShows(videos, { includeAnime: false }).map(showToCatalogItem);
    const animeMovies = videos.filter((v) => v.content_type === "anime");
    const animeShows = buildSeriesShows(videos.filter((v) => v.content_type === "anime_series"))
      .map(showToCatalogItem);
    return { movies, seriesShows, animeMovies, animeShows };
  }, [videos]);

  const catalogCount =
    grouped.movies.length +
    grouped.seriesShows.length +
    grouped.animeMovies.length +
    grouped.animeShows.length;
  const totalHits = catalogCount + channels.length;

  return (
    <Shell wide>
      <div className="search-page">
        <header className="search-page-head">
          <p className="search-page-kicker">Búsqueda global</p>
          <h1>{q.length >= MIN_Q ? `“${q}”` : "Buscar en Casasm"}</h1>
          <p className="muted search-page-lead">
            {q.length < MIN_Q
              ? "Escribe al menos 2 letras en la barra superior para buscar películas, series, anime y canales en vivo."
              : loading
                ? "Buscando en tu catálogo y en IPTV…"
                : totalHits
                  ? `${totalHits} resultado${totalHits === 1 ? "" : "s"} encontrado${totalHits === 1 ? "" : "s"}`
                  : "Sin resultados. Prueba otro nombre o canal."}
          </p>
        </header>

        {error ? <p className="error">{error}</p> : null}

        {q.length >= MIN_Q && loading ? (
          <div className="search-page-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="search-hit search-hit-skeleton" />
            ))}
          </div>
        ) : null}

        {q.length >= MIN_Q && !loading && channels.length ? (
          <section className="search-section">
            <div className="search-section-head">
              <h2>Canales en vivo</h2>
              <Link to={`/iptv?q=${encodeURIComponent(q)}`} className="search-section-link">
                Ver todos en IPTV
                {channelTotal > channels.length ? ` (${channelTotal})` : ""}
              </Link>
            </div>
            <div className="search-page-grid">
              {channels.map((ch) => (
                <ChannelHit key={ch.id} channel={ch} />
              ))}
            </div>
          </section>
        ) : null}

        {q.length >= MIN_Q && !loading && grouped.movies.length ? (
          <section className="search-section">
            <div className="search-section-head">
              <h2>Películas</h2>
              <Link to={`/peliculas?q=${encodeURIComponent(q)}`} className="search-section-link">
                Ver en películas
              </Link>
            </div>
            <div className="search-page-grid">
              {grouped.movies.slice(0, LIMIT).map((v) => (
                <CatalogHit key={v.id} item={v} />
              ))}
            </div>
          </section>
        ) : null}

        {q.length >= MIN_Q && !loading && grouped.seriesShows.length ? (
          <section className="search-section">
            <div className="search-section-head">
              <h2>Series</h2>
              <Link to={`/series?q=${encodeURIComponent(q)}`} className="search-section-link">
                Ver en series
              </Link>
            </div>
            <div className="search-page-grid">
              {grouped.seriesShows.slice(0, LIMIT).map((v) => (
                <CatalogHit key={v.catalog_key || v.id} item={{ ...v, kind: "series_show" }} />
              ))}
            </div>
          </section>
        ) : null}

        {q.length >= MIN_Q && !loading && (grouped.animeMovies.length || grouped.animeShows.length) ? (
          <section className="search-section">
            <div className="search-section-head">
              <h2>Anime</h2>
              <Link to={`/anime?q=${encodeURIComponent(q)}`} className="search-section-link">
                Ver en anime
              </Link>
            </div>
            <div className="search-page-grid">
              {grouped.animeShows.slice(0, 12).map((v) => (
                <CatalogHit key={`s-${v.catalog_key}`} item={{ ...v, kind: "series_show" }} />
              ))}
              {grouped.animeMovies.slice(0, 12).map((v) => (
                <CatalogHit key={`m-${v.id}`} item={v} />
              ))}
            </div>
          </section>
        ) : null}

        {q.length >= MIN_Q && !loading && !totalHits && !error ? (
          <div className="search-empty">
            <p className="search-empty-icon" aria-hidden="true">
              🔍
            </p>
            <p>No encontramos “{q}” en el catálogo ni en IPTV.</p>
            <button type="button" className="search-empty-btn" onClick={() => navigate("/iptv")}>
              Explorar canales en vivo
            </button>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
